import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Building2, AlertTriangle, TrendingUp, LogOut, Home, Baby, Calendar, ExternalLink, CreditCard, Receipt, ChevronDown, ChevronUp, ChevronRight, Users, Globe, ClipboardList, CalendarDays, FileText, X, Check, Clock, Edit3, CloudSun, Wind, Thermometer, Mail, Menu as MenuIcon, Plus, Upload, Loader2, CalendarPlus, ShoppingCart, Crown, Shield, Compass, Search } from 'lucide-react';
import DailyBriefingBar from '../components/DailyBriefingBar';

const fmtUK = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };


const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = ['owner', 'staff', 'accountant', 'investor'];

// Role-based visibility permissions (mirrors backend permissions.py)
const ROLE_VISIBILITY = {
  owner:      { financial: true, personal: true, infrastructure: true, editTasks: true, documents: true },
  staff:      { financial: false, personal: false, infrastructure: false, editTasks: false, documents: true },
  accountant: { financial: true, personal: false, infrastructure: true, editTasks: true, documents: true },
  investor:   { financial: true, personal: false, infrastructure: false, editTasks: false, documents: false },
};


const TomorrowGlance = () => {
  const [items, setItems] = React.useState([]);
  const [completing, setCompleting] = React.useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tStr = tomorrow.toISOString().split('T')[0];
    axios.get(`${API_URL}/api/planner/unified?months=1`, { withCredentials: true })
      .then(res => {
        const all = res.data?.items || [];
        setItems(all.filter(i => i.date === tStr));
      }).catch(console.error);
  }, []);

  const complete = async (item) => {
    setCompleting(item.id);
    try {
      if (item.type === 'task') await axios.put(`${API_URL}/api/tasks/${item.id}`, { status: 'completed' }, { withCredentials: true });
      else if (item.type === 'personal') await axios.put(`${API_URL}/api/personal/${item.id}`, { status: 'done' }, { withCredentials: true });
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (e) { console.error(e); }
    setCompleting(null);
  };

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const label = tomorrow.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const personal = items.filter(i => i.type === 'personal');
  const tasks = items.filter(i => i.type !== 'personal');

  if (items.length === 0) return null;

  return (
    <div className="bg-white border overflow-hidden mb-5" style={{ borderColor: '#D4AF3740' }} data-testid="tomorrow-glance">
      <div className="px-4 py-2.5 flex items-center justify-between" style={{ backgroundColor: '#FAF7F2', borderBottom: '1px solid #E5E1DB' }}>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4" style={{ color: '#D4AF37' }} />
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#B8860B' }}>Tomorrow — {label}</span>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-sm" style={{ backgroundColor: '#D4AF3715', color: '#B8860B' }}>{items.length} items</span>
      </div>
      <div className="divide-y" style={{ borderColor: '#F5F1EB' }}>
        {personal.map(item => (
          <div key={item.id} className="px-4 py-2.5 flex items-center gap-3 group" data-testid={`tomorrow-${item.id}`}>
            <button onClick={() => complete(item)} className="w-5 h-5 rounded-sm border flex-shrink-0 flex items-center justify-center hover:bg-green-100 transition-colors" style={{ borderColor: '#EC4899' }}>
              {completing === item.id ? <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#EC4899' }} /> : <Check className="w-3 h-3 opacity-0 group-hover:opacity-50" style={{ color: '#16A34A' }} />}
            </button>
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#EC4899' }} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium" style={{ color: '#2C1A0E' }}>{item.title}</p>
              {item.time && <p className="text-[10px] font-mono" style={{ color: '#8B7355' }}>{item.time}</p>}
            </div>
          </div>
        ))}
        {tasks.length > 0 && (
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-[10px]" style={{ color: '#8B7355' }}>+ {tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
            <button onClick={() => navigate('/planner')} className="text-[10px] font-medium" style={{ color: '#B8860B' }}>View in Planner</button>
          </div>
        )}
      </div>
    </div>
  );
};

const ComplianceAlerts = () => {
  const [deadlines, setDeadlines] = React.useState([]);
  const [expanded, setExpanded] = React.useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    axios.get(`${API_URL}/api/hmrc/deadlines/upcoming`, { withCredentials: true })
      .then(res => {
        const sixtyDays = new Date(Date.now() + 60 * 86400000).toISOString().split('T')[0];
        const seen = new Set();
        const urgent = res.data.filter(d => {
          if (!d.due_date || d.due_date > sixtyDays) return false;
          if (d.status !== 'upcoming') return false;
          if (d.title.startsWith('REMINDER:')) return false;
          const key = `${d.title}-${d.due_date}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }).sort((a, b) => a.due_date.localeCompare(b.due_date));
        setDeadlines(urgent);
      }).catch(console.error);
  }, []);

  if (deadlines.length === 0) return null;

  return (
    <div className="bg-white border overflow-hidden mb-5" style={{ borderColor: '#DC262640' }} data-testid="compliance-alerts">
      <div className="px-4 py-2.5 flex items-center justify-between" style={{ backgroundColor: '#FEF2F2', borderBottom: '1px solid #FECACA' }}>
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4" style={{ color: '#DC2626' }} />
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#DC2626' }}>HMRC / Companies House — Filing Deadlines</span>
        </div>
        <button onClick={() => navigate('/hmrc')} className="text-[10px] font-medium" style={{ color: '#B8860B' }}>View HMRC</button>
      </div>
      <div>
        {deadlines.map(d => {
          const days = Math.ceil((new Date(d.due_date) - new Date()) / 86400000);
          const isOpen = expanded === d.id;
          return (
            <div key={d.id} data-testid={`compliance-${d.id}`}>
              <button
                onClick={() => setExpanded(isOpen ? null : d.id)}
                className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-amber-50/30 transition-colors"
                style={{ borderBottom: '1px solid #FEF2F2' }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: days <= 7 ? '#DC2626' : days <= 14 ? '#D97706' : '#B8860B' }} />
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: '#2C1A0E' }}>{d.company_name ? `${d.company_name}: ` : ''}{d.title}</p>
                    <p className="text-[10px] font-mono" style={{ color: '#8B7355' }}>{fmtUK(d.due_date)}</p>
                  </div>
                </div>
                <span className="text-xs font-bold px-2 py-1 rounded-sm flex-shrink-0 ml-2" style={{
                  backgroundColor: days <= 7 ? '#FEF2F2' : days <= 14 ? '#FFFBEB' : '#FAF7F2',
                  color: days <= 7 ? '#DC2626' : days <= 14 ? '#D97706' : '#B8860B'
                }}>{days}D</span>
              </button>
              {isOpen && (
                <div className="px-4 py-3 mx-4 mb-2 rounded-sm" style={{ backgroundColor: '#FAF7F2', border: '1px solid #E8E0D4' }}>
                  {d.company_name && <p className="text-xs font-semibold mb-1" style={{ color: '#2C1A0E' }}>{d.company_name}</p>}
                  <p className="text-xs leading-relaxed mb-2" style={{ color: '#6B5B4E' }}>{d.action || 'Check your HMRC Government Gateway account.'}</p>
                  <div className="flex gap-2">
                    {d.company_id && (
                      <button onClick={() => navigate(`/hmrc/companies/${d.company_id}`)} className="text-[10px] font-medium px-2.5 py-1 rounded-sm" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid={`goto-company-${d.id}`}>
                        View Company
                      </button>
                    )}
                    <a href="https://www.gov.uk/log-in-register-hmrc-online-services" target="_blank" rel="noopener noreferrer" className="text-[10px] font-medium px-2.5 py-1 rounded-sm" style={{ backgroundColor: '#FFF', color: '#B8860B', border: '1px solid #E8E0D4' }}>
                      HMRC Gateway
                    </a>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};




const Dashboard = () => {
  const { user, logout, switchRole, viewAsRole } = useAuth();
  
  // Role-based permission check
  const activeRole = viewAsRole || user?.role || 'owner';
  const can = (perm) => ROLE_VISIBILITY[activeRole]?.[perm] ?? false;
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [checklistSummary, setChecklistSummary] = useState([]);
  const [tasksSummary, setTasksSummary] = useState({ open: 0, in_progress: 0, overdue: [] });
  const [contentSummary, setContentSummary] = useState({ draft: 0, scheduled: 0, upcoming_this_week: [] });
  const [datedPersonal, setDatedPersonal] = useState({ overdue: [], upcoming: [] });
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('companies');
  const [expandedStat, setExpandedStat] = useState(null);
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [weather, setWeather] = useState(null);
  const [editingPlannerBlock, setEditingPlannerBlock] = useState(null);
  const [expandedCompanyTasks, setExpandedCompanyTasks] = useState('__first__');
  const [showPersonalMenu, setShowPersonalMenu] = useState(false);
  const [showHamburger, setShowHamburger] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showTasksDropdown, setShowTasksDropdown] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickTask, setQuickTask] = useState({ type: 'task', company_id: '', title: '', description: '', priority: 'medium', category: 'action', due_date: '', end_date: '', time: '', end_time: '', recurring: '', is_personal: false, personal_category: 'admin' });
  const [quickSaving, setQuickSaving] = useState(false);
  const [showDocParse, setShowDocParse] = useState(false);
  const [docParsing, setDocParsing] = useState(false);
  const [parsedItems, setParsedItems] = useState([]);
  const [docSaving, setDocSaving] = useState(false);
  const [quickTextInput, setQuickTextInput] = useState('');
  const [quickTextParsing, setQuickTextParsing] = useState(false);
  const [quickParsedItems, setQuickParsedItems] = useState([]);
  const [quickBulkSaving, setQuickBulkSaving] = useState(false);
  const [aiMode, setAiMode] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchData();
    axios.get(`${API_URL}/api/weather`).then(r => setWeather(r.data)).catch(() => {});
  }, []);

  const api = (url) => axios.get(`${API_URL}${url}`, { withCredentials: true }).catch(() => axios.get(`${API_URL}${url}`));

  const fetchData = async () => {
    try {
      const [companiesRes, deadlinesRes, transactionsRes, checklistRes, tasksRes, contentRes, datedRes, watchlistRes] = await Promise.all([
        api('/api/companies').catch(() => ({ data: [] })),
        api('/api/deadlines').catch(() => ({ data: [] })),
        api('/api/transactions').catch(() => ({ data: [] })),
        api('/api/checklist/summary').catch(() => ({ data: [] })),
        api('/api/tasks/summary').catch(() => ({ data: { open: 0, in_progress: 0, overdue: [] } })),
        api('/api/content-calendar/summary').catch(() => ({ data: { draft: 0, scheduled: 0, upcoming_this_week: [] } })),
        api('/api/personal/dated').catch(() => ({ data: { overdue: [], upcoming: [] } })),
        api('/api/personal/watchlist').catch(() => ({ data: [] }))
      ]);
      setCompanies(companiesRes.data);
      setDeadlines(deadlinesRes.data);
      setTransactions(transactionsRes.data);
      setChecklistSummary(checklistRes.data);
      setTasksSummary(tasksRes.data);
      setContentSummary(contentRes.data);
      setDatedPersonal(datedRes.data);
      setWatchlist(watchlistRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim() || query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API_URL}/api/search?q=${encodeURIComponent(query)}`, { withCredentials: true });
      setSearchResults(res.data.results || []);
    } catch (err) { console.error(err); }
    setSearching(false);
  };

  const TYPE_ICONS = { task: ClipboardList, personal: Home, company: Building2, hmrc: Crown, hmrc_deadline: Crown, opportunity: Compass, content: FileText, calendar: CalendarDays };
  const TYPE_COLORS = { task: '#B8860B', personal: '#EC4899', company: '#2C1A0E', hmrc: '#D4AF37', hmrc_deadline: '#DC2626', opportunity: '#0D9488', content: '#8B5CF6', calendar: '#0EA5E9' };


  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleQuickAddTask = async (e) => {
    e.preventDefault();
    if (!quickTask.title) return;
    if (quickTask.type === 'task' && !quickTask.company_id) return;
    setQuickSaving(true);
    try {
      if (quickTask.type === 'personal') {
        await axios.post(`${API_URL}/api/personal`, {
          category: quickTask.personal_category || 'admin',
          subcategory: 'general',
          title: quickTask.title,
          date: quickTask.due_date || new Date().toISOString().split('T')[0],
          end_date: quickTask.end_date || '',
          time: quickTask.time || '',
          notes: (quickTask.description || '') + (quickTask.end_time ? ` [End: ${quickTask.end_time}]` : '') + (quickTask.recurring ? ` [Recurring: ${quickTask.recurring}]` : ''),
          status: 'pending',
          recurring: quickTask.recurring || null,
        }, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/tasks`, {
          company_id: quickTask.company_id,
          title: quickTask.title,
          description: quickTask.description,
          priority: quickTask.priority,
          category: quickTask.category,
          due_date: quickTask.due_date,
          end_date: quickTask.end_date || '',
          time: quickTask.time || '',
          end_time: quickTask.end_time || '',
          recurring: quickTask.recurring || null,
          status: 'open',
          assigned_to: user?.name || 'Naledi',
        }, { withCredentials: true });
      }
      setShowQuickAdd(false);
      setQuickTask({ type: 'task', company_id: '', title: '', description: '', priority: 'medium', category: 'action', due_date: '', end_date: '', time: '', end_time: '', recurring: '', is_personal: false, personal_category: 'admin' });
      setQuickTextInput('');
      setQuickParsedItems([]);
      fetchData();
    } catch (err) {
      console.error('Error adding task:', err);
    } finally {
      setQuickSaving(false);
    }
  };

  const handleQuickTextParse = async () => {
    if (!quickTextInput.trim()) return;
    setQuickTextParsing(true);
    try {
      const res = await axios.post(`${API_URL}/api/parse-text`, { text: quickTextInput }, { withCredentials: true });
      if (res.data.status === 'success' && res.data.items?.length > 0) {
        setQuickParsedItems(res.data.items);
      }
    } catch (err) {
      console.error('Error parsing text:', err);
    } finally {
      setQuickTextParsing(false);
    }
  };

  const handleBulkAddParsed = async () => {
    if (quickParsedItems.length === 0) return;
    setQuickBulkSaving(true);
    try {
      await axios.post(`${API_URL}/api/bulk-add-items`, { items: quickParsedItems }, { withCredentials: true });
      setShowQuickAdd(false);
      setQuickTextInput('');
      setQuickParsedItems([]);
      fetchData();
    } catch (err) {
      console.error('Error bulk adding:', err);
    } finally {
      setQuickBulkSaving(false);
    }
  };

  const handleSnooze = async (taskId, snoozeTo) => {
    try {
      await axios.post(`${API_URL}/api/tasks/${taskId}/snooze`, { snooze_to: snoozeTo }, { withCredentials: true });
      fetchData();
    } catch (err) {
      console.error('Error snoozing task:', err);
    }
  };

  const [syncingTaskId, setSyncingTaskId] = useState(null);
  const handleSyncToCalendar = async (task, companyName) => {
    setSyncingTaskId(task.id);
    try {
      const res = await axios.post(`${API_URL}/api/calendar/sync-task`, {
        title: task.title,
        due_date: task.due_date,
        time: task.time || '',
        end_time: task.end_time || '',
        description: task.description || '',
        company_name: companyName || '',
      }, { withCredentials: true, responseType: 'blob' });
      // Download the .ics file
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/calendar' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(task.title || 'event').slice(0, 30).replace(/\s/g, '_')}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSyncingTaskId('done-' + task.id);
      setTimeout(() => setSyncingTaskId(null), 2000);
    } catch (err) {
      console.error('Error syncing to calendar:', err);
      setSyncingTaskId(null);
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocParsing(true);
    setShowDocParse(true);
    setParsedItems([]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/parse-document`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
      if (res.data.items?.length) {
        setParsedItems(res.data.items.map(item => ({ ...item, selected: true })));
      }
    } catch (err) {
      console.error('Error parsing document:', err);
    } finally {
      setDocParsing(false);
      e.target.value = '';
    }
  };

  const handleBulkAdd = async () => {
    const selected = parsedItems.filter(i => i.selected);
    if (!selected.length) return;
    setDocSaving(true);
    try {
      await axios.post(`${API_URL}/api/bulk-add-items`, { items: selected }, { withCredentials: true });
      setShowDocParse(false);
      setParsedItems([]);
      fetchData();
    } catch (err) {
      console.error('Error adding items:', err);
    } finally {
      setDocSaving(false);
    }
  };

  const getTodaySchedule = () => {
    const today = new Date().toDateString();
    const items = [];
    
    // Company deadlines due today
    deadlines.filter(d => new Date(d.date).toDateString() === today && d.status !== 'completed').forEach(d => {
      items.push({ ...d, source: 'deadline', deadline_type: d.company_name || 'Deadline' });
    });
    
    // Pending transactions due today
    transactions.filter(t => new Date(t.due_date).toDateString() === today && t.status === 'pending').forEach(t => {
      items.push({ ...t, source: 'transaction', deadline_type: 'Payment', date: t.due_date });
    });
    
    // Overdue company tasks (these need attention today)
    (tasksSummary.overdue || []).forEach(t => {
      items.push({ ...t, source: 'task', deadline_type: t.company_name, date: t.due_date });
    });
    
    // Company tasks due today
    (tasksSummary.due_soon || []).filter(t => new Date(t.due_date).toDateString() === today).forEach(t => {
      items.push({ ...t, source: 'task', deadline_type: t.company_name, date: t.due_date });
    });
    
    // Overdue personal items (need attention today)
    (datedPersonal.overdue || []).filter(p => p.status !== 'done' && p.status !== 'completed').forEach(p => {
      items.push({ ...p, source: 'personal', deadline_type: `${p.subcategory || p.category}`, date: p.date });
    });
    
    // Personal items due today
    (datedPersonal.upcoming || []).filter(p => new Date(p.date).toDateString() === today && p.status !== 'done' && p.status !== 'completed').forEach(p => {
      items.push({ ...p, source: 'personal', deadline_type: `${p.subcategory || p.category}`, date: p.date });
    });
    
    return items.sort((a, b) => new Date(a.date || a.due_date) - new Date(b.date || b.due_date));
  };

  const getUpcomingThisWeek = () => {
    const today = new Date();
    const endOfWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const items = [];
    // Deadlines this week
    deadlines.filter(d => {
      const date = new Date(d.date);
      return date >= today && date <= endOfWeek && d.status !== 'completed';
    }).forEach(d => items.push(d));
    // Tasks due this week
    [...(tasksSummary.due_soon || []), ...(tasksSummary.overdue || [])].forEach(t => {
      const date = new Date(t.due_date);
      if (date <= endOfWeek) items.push(t);
    });
    // Personal items this week
    (datedPersonal.upcoming || []).filter(p => {
      const date = new Date(p.date);
      return date >= today && date <= endOfWeek;
    }).forEach(p => items.push(p));
    return items;
  };

  const getOverdueInvoices = () => {
    const today = new Date();
    return transactions.filter(t => 
      t.direction === 'in' && 
      t.status === 'pending' && 
      new Date(t.due_date) < today
    );
  };

  const getOverdueBills = () => {
    const today = new Date();
    return transactions.filter(t => 
      t.direction === 'out' && 
      t.status === 'pending' && 
      new Date(t.due_date) < today
    );
  };

  const personalCategories = [
    { 
      name: 'Family', 
      icon: Home, 
      color: '#B8860B', 
      path: '/personal/family',
      subcategories: [
        'Subscriptions',
        'Bills', 
        'Appointments',
        'Medical Appointments',
        'To-Dos',
        'Admin & Upkeep'
      ]
    },
    { 
      name: 'Kids', 
      icon: Baby, 
      color: '#EC4899', 
      path: '/personal/kids',
      subcategories: [
        'School Holidays',
        'Clubs',
        'Homework & School Work',
        'Appointments'
      ]
    }
  ];

  // Edit handlers for overdue tasks and personal items
  const openEditModal = (item, type) => {
    setEditModal({ item, type });
    if (type === 'task') {
      setEditForm({ title: item.title || '', due_date: item.due_date || '', end_date: item.end_date || '', time: item.time || '', end_time: item.end_time || '', description: item.description || '', priority: item.priority || 'medium', category: item.category || 'action', status: item.status || 'open' });
    } else {
      setEditForm({ title: item.title || '', date: item.date || '', end_date: item.end_date || '', time: item.time || '', notes: item.notes || '', status: item.status || 'active' });
    }
  };

  const handleSaveEdit = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      const { item, type } = editModal;
      if (type === 'task') {
        await axios.put(`${API_URL}/api/tasks/${item.id}`, {
          title: editForm.title,
          due_date: editForm.due_date || null,
          end_date: editForm.end_date || null,
          time: editForm.time || null,
          end_time: editForm.end_time || null,
          description: editForm.description || '',
          priority: editForm.priority,
          category: editForm.category,
          status: editForm.status,
        }, { withCredentials: true });
      } else {
        await axios.put(`${API_URL}/api/personal/${item.id}`, {
          title: editForm.title,
          date: editForm.date || null,
          end_date: editForm.end_date || null,
          time: editForm.time || null,
          notes: editForm.notes,
          status: editForm.status,
        }, { withCredentials: true });
      }
      setEditModal(null);
      fetchData();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!editModal) return;
    const { item, type } = editModal;
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      if (type === 'task') {
        await axios.delete(`${API_URL}/api/tasks/${item.id}`, { withCredentials: true });
      } else {
        await axios.delete(`${API_URL}/api/personal/${item.id}`, { withCredentials: true });
      }
      setEditModal(null);
      fetchData();
    } catch (err) { console.error('Delete failed:', err); }
  };

  const handleMarkDone = async (item, type) => {
    try {
      if (type === 'task') {
        await axios.put(`${API_URL}/api/tasks/${item.id}`, { status: 'done' }, { withCredentials: true });
      } else {
        await axios.put(`${API_URL}/api/personal/${item.id}`, { status: 'completed' }, { withCredentials: true });
      }
      fetchData();
    } catch (err) {
      console.error('Mark done failed:', err);
    }
  };

  const criticalDeadlines = deadlines.filter(d => {
    const today = new Date();
    const next7Days = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const deadlineDate = new Date(d.date);
    return deadlineDate >= today && deadlineDate <= next7Days && d.status !== 'completed';
  });
  const todaySchedule = getTodaySchedule();
  const weeklyItems = getUpcomingThisWeek();

  // Company logo map
  const COMPANY_LOGOS = {
    'Spaza Global': '/spaza-global-logo.png',
    'Spaza Luxe Escapes': '/spaza-luxe-escapes-logo.png',
    'Star Global Remit': '/sgr-logo.png',
    'Star Talent Group': '/stg-logo.jpg',
    'KiddiPlay': '/kiddiplay-logo.jpg',
    'Maluti Holdings': '/maluti-logo-web.png',
    'GrabbyBag': '/grabbybag-logo.png',
  };
  const COMPANY_LOGO_BG = {
    'Spaza Global': '#0A0A0A',
    'Spaza Luxe Escapes': '#FBF8F3',
    'Star Global Remit': '#FAFAFA',
    'Star Talent Group': '#1A2F4A',
    'KiddiPlay': '#FFF5FB',
    'Maluti Holdings': '#FFFFFF',
    'GrabbyBag': '#F5F5F5',
  };

  // Group tasks by company
  const tasksByCompany = {};
  const allTasks = [...(tasksSummary.overdue || []), ...(tasksSummary.due_soon || [])];
  allTasks.forEach(t => {
    const cn = t.company_name || 'Unassigned';
    if (!tasksByCompany[cn]) tasksByCompany[cn] = [];
    tasksByCompany[cn].push(t);
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF6' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#D4AF37' }}></div>
          <p className="mt-4" style={{ color: '#8B7355' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAF6', fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="dashboard">

      {/* ═══ CLEAN HEADER — Logo + Search + Menu ═══ */}
      <header className="bg-white sticky top-0 z-40" style={{ borderBottom: '1px solid #E8E0D4' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 flex-shrink-0 no-underline">
            <img src="/maluti-logo-web.png" alt="Maluti Holdings" className="h-8 object-contain" />
            <div className="hidden sm:block">
              <h1 className="text-xs font-medium tracking-wide leading-tight" style={{ color: '#2C1A0E' }}>Maluti Holdings</h1>
              <p className="text-[8px] tracking-widest uppercase" style={{ color: '#8B7355' }}>Operations Suite</p>
            </div>
          </a>

          {/* Search */}
          <button onClick={() => setShowSearch(true)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border transition-colors hover:bg-amber-50 ml-2" style={{ borderColor: '#E8E0D4' }} data-testid="global-search-btn">
            <Search className="w-3.5 h-3.5" style={{ color: '#8B7355' }} />
            <span className="text-[10px] hidden sm:inline" style={{ color: '#8B7355' }}>Search</span>
          </button>

          <div className="flex-1" />

          {/* Quick access: Planner + Email */}
          <button onClick={() => navigate('/planner')} className="flex items-center gap-1 px-2 py-1.5 rounded transition-colors hover:bg-amber-50 mr-1" data-testid="header-planner-btn">
            <CalendarDays className="w-4 h-4" style={{ color: '#B8860B' }} />
            <span className="text-xs font-medium hidden sm:inline" style={{ color: '#B8860B' }}>Planner</span>
          </button>
          <button onClick={() => navigate('/email')} className="flex items-center gap-1 px-2 py-1.5 rounded transition-colors hover:bg-amber-50 mr-1" data-testid="header-email-btn">
            <Mail className="w-4 h-4" style={{ color: '#B8860B' }} />
            <span className="text-xs font-medium hidden sm:inline" style={{ color: '#B8860B' }}>Email</span>
          </button>

          {/* Profile */}
          <div className="flex items-center gap-2 cursor-pointer mr-2" onClick={() => navigate('/profile')} data-testid="header-profile-link">
            {user?.profile_pic ? (
              <img src={user.profile_pic} alt="Profile" className="w-8 h-8 rounded-full object-cover border-2" style={{ borderColor: '#D4AF37' }} />
            ) : (
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: '#D4AF37', color: '#fff' }}>
                {(user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>
            )}
          </div>

          {/* ☰ Hamburger Menu */}
          <button onClick={() => setShowHamburger(!showHamburger)} className="p-2 rounded-sm border transition-colors hover:bg-amber-50" style={{ borderColor: '#E8E0D4' }} data-testid="hamburger-menu-btn">
            <MenuIcon className="w-5 h-5" style={{ color: '#2C1A0E' }} />
          </button>
        </div>

        {/* ═══ DROPDOWN MENU ═══ */}
        {showHamburger && (
          <div className="absolute right-0 top-full w-full sm:w-72 sm:right-4 bg-white border-t sm:border shadow-lg z-50 max-h-[80vh] overflow-y-auto" style={{ borderColor: '#E8E0D4' }} data-testid="hamburger-dropdown">
            <div className="px-4 py-2" style={{ borderBottom: '1px solid #F0EBE3' }}>
              <p className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: '#B8860B' }}>Business</p>
            </div>
            {[
              { icon: Building2, label: 'My Companies', path: '/weekly-briefing', color: '#2C1A0E' },
              { icon: ClipboardList, label: `Tasks (${tasksSummary.open} open)`, path: '/weekly-briefing', color: '#B8860B' },
              { icon: Compass, label: 'Maluti Navigator', path: '/navigator', color: '#0D9488' },
              { icon: Crown, label: 'HMRC Control', path: '/hmrc', color: '#D4AF37' },
              { icon: CreditCard, label: 'Financials', path: '/financial-dashboard', color: '#0D9488' },
              { icon: TrendingUp, label: 'BI Dashboard', path: '/bi-dashboard', color: '#0D9488' },
              { icon: Shield, label: 'CEO Dashboard', path: '/ceo', color: '#2C1A0E' },
              { icon: FileText, label: 'Content Calendar', path: '/content-calendar', color: '#8B5CF6' },
            ].map(item => (
              <button key={item.path + item.label} onClick={() => { navigate(item.path); setShowHamburger(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-50/40 transition-colors" style={{ borderBottom: '1px solid #F8F5F0' }}>
                <item.icon className="w-4 h-4" style={{ color: item.color }} />
                <span className="text-sm" style={{ color: '#2C1A0E' }}>{item.label}</span>
              </button>
            ))}

            <div className="px-4 py-2" style={{ borderBottom: '1px solid #F0EBE3', borderTop: '1px solid #E8E0D4' }}>
              <p className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: '#EC4899' }}>Personal</p>
            </div>
            {personalCategories.map(cat => (
              <button key={cat.name} onClick={() => { navigate(cat.path); setShowHamburger(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-50/40 transition-colors" style={{ borderBottom: '1px solid #F8F5F0' }}>
                <cat.icon className="w-4 h-4" style={{ color: cat.color }} />
                <span className="text-sm" style={{ color: '#2C1A0E' }}>{cat.name}</span>
              </button>
            ))}
            <button onClick={() => { navigate('/shopping'); setShowHamburger(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-50/40 transition-colors" style={{ borderBottom: '1px solid #F8F5F0' }}>
              <ShoppingCart className="w-4 h-4" style={{ color: '#8B7355' }} />
              <span className="text-sm" style={{ color: '#2C1A0E' }}>Shopping List</span>
            </button>

            <div className="px-4 py-2" style={{ borderTop: '1px solid #E8E0D4' }}>
              <p className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: '#8B7355' }}>Account</p>
            </div>
            <button onClick={() => { navigate('/weekly-briefing'); setShowHamburger(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-50/40 transition-colors" style={{ borderBottom: '1px solid #F8F5F0' }}>
              <FileText className="w-4 h-4" style={{ color: '#B8860B' }} />
              <span className="text-sm" style={{ color: '#2C1A0E' }}>Weekly Briefing</span>
            </button>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-50/40 transition-colors">
              <LogOut className="w-4 h-4" style={{ color: '#DC2626' }} />
              <span className="text-sm" style={{ color: '#DC2626' }}>Sign Out</span>
            </button>
          </div>
        )}
      </header>

      {/* Role Preview Banner */}
      {activeRole !== 'owner' && (
        <div className="text-center py-2 text-xs font-medium tracking-wide" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="role-preview-banner">
          Viewing as <span className="uppercase font-bold">{activeRole}</span>
          <button onClick={() => switchRole('owner')} className="ml-3 underline text-[10px] opacity-80 hover:opacity-100">Switch back</button>
        </div>
      )}

      {/* Critical Alert */}
      {criticalDeadlines.length > 0 && (
        <div style={{ backgroundColor: '#D4AF37', color: 'white' }} data-testid="critical-alert-banner">
          <div className="max-w-7xl mx-auto px-6 py-2.5">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <p className="text-xs font-medium">{criticalDeadlines.length} deadline(s) within 7 days: {criticalDeadlines.map(d => d.title).join(', ')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Greeting slim line */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-1.5 flex items-center gap-3" style={{ backgroundColor: '#FAFAF6' }}>
        <span className="text-xs font-medium" style={{ color: '#2C1A0E' }}>
          {new Date().getHours() < 12 ? 'Good Morning' : new Date().getHours() < 17 ? 'Good Afternoon' : 'Good Evening'}
        </span>
        <span className="text-[10px]" style={{ color: '#8B7355' }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
        </span>
        {weather && !weather.error && (
          <span className="text-[10px] ml-auto" style={{ color: '#8B7355' }}>
            {weather.temperature != null ? `${weather.temperature}°C` : ''} {weather.description || ''}
          </span>
        )}
      </div>

      {/* Daily Briefing Alert Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-1" style={{ backgroundColor: '#FAFAF6' }}>
        <DailyBriefingBar />
      </div>

      <div className="flex">
        {/* ═══ MAIN CONTENT ═══ */}
        <div className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-4">

          {/* ══ HMRC DEADLINES ══ */}
          <ComplianceAlerts />

          {/* ══ TODAY'S PLANNER — Big, readable ══ */}
          {(() => {
            const dayOfWeek = new Date().getDay();
            const currentHour = new Date().getHours();
            const dayThemes = { 0: 'Rest Day', 1: 'Spaza Ecosystem', 2: 'Spaza Admin + Maluti', 3: 'STG & SGR', 4: 'Sales & Meetings', 5: 'KiddiPlay + Content', 6: 'Family & Home' };
            const theme = dayThemes[dayOfWeek] || '';
            const DAY_PLANS = {
              0: [{ time: '10:00', label: 'Family time', color: '#8B7355' }],
              1: [{ time: '08:30', label: 'School drop-off', color: '#8B7355', termOnly: true }, { time: '09:15', label: 'Spaza Global — suppliers, orders, trade', color: '#C2410C' }, { time: '11:00', label: 'MAPS — pilot, buyer outreach', color: '#0D9488' }, { time: '12:30', label: 'Lunch', color: '#16A34A' }, { time: '13:15', label: 'Spaza Luxe Escapes — travel, bookings', color: '#7C3AED' }, { time: '15:15', label: 'School pick-up', color: '#8B7355', termOnly: true }, { time: '16:45', label: "Kid's Swim Lessons", color: '#1E3A8A' }],
              2: [{ time: '08:30', label: 'School drop-off', color: '#8B7355', termOnly: true }, { time: '09:15', label: 'Spaza Finance — invoices, Xero, VAT', color: '#C2410C' }, { time: '11:00', label: 'Spaza admin & compliance', color: '#C2410C' }, { time: '12:30', label: 'Lunch', color: '#16A34A' }, { time: '13:15', label: 'Maluti Strategy — advisory, BID review', color: '#B8860B' }, { time: '14:30', label: 'Maluti — speaking, portfolio, contracts', color: '#B8860B' }, { time: '15:15', label: 'School pick-up', color: '#8B7355', termOnly: true }],
              3: [{ time: '08:30', label: 'School drop-off', color: '#8B7355', termOnly: true }, { time: '09:15', label: 'STG — bookings, artists, events, publishing', color: '#1E3A5F' }, { time: '11:00', label: 'SGR — platform, regulatory, partnerships', color: '#B91C1C' }, { time: '12:30', label: 'Lunch', color: '#16A34A' }, { time: '13:15', label: 'STG & SGR — accounts, admin, compliance', color: '#1E3A5F' }, { time: '15:15', label: 'School pick-up', color: '#8B7355', termOnly: true }],
              4: [{ time: '08:30', label: 'School drop-off', color: '#8B7355', termOnly: true }, { time: '09:15', label: 'Sales calls — all businesses', color: '#0D9488' }, { time: '11:00', label: 'Partnership pitches', color: '#0D9488' }, { time: '12:30', label: 'Lunch', color: '#16A34A' }, { time: '13:15', label: 'Follow-ups — chase proposals', color: '#0D9488' }, { time: '14:30', label: 'Close deals, confirmations', color: '#0D9488' }, { time: '15:15', label: 'School pick-up', color: '#8B7355', termOnly: true }],
              5: [{ time: '08:30', label: 'School drop-off', color: '#8B7355', termOnly: true }, { time: '09:15', label: 'KiddiPlay — partner outreach, listings', color: '#EC4899' }, { time: '11:00', label: 'KiddiPlay — community, campaigns', color: '#EC4899' }, { time: '12:30', label: 'Lunch', color: '#16A34A' }, { time: '13:15', label: 'Content — all businesses social media', color: '#D4AF37' }, { time: '14:30', label: 'Content — newsletters, scheduling', color: '#D4AF37' }, { time: '15:20', label: 'Niamh Mandarin (3:20-4:20)', color: '#9333EA', termOnly: true }],
              6: [{ time: '09:00', label: 'Family activity', color: '#8B7355' }],
            };
            // Term dates: Robert Fitzroy Academy 2026-27
            const TERM_DATES = [
              ['2026-09-03', '2026-10-21'], // Autumn 1
              ['2026-11-02', '2026-12-18'], // Autumn 2
              ['2027-01-04', '2027-02-12'], // Spring 1
              ['2027-02-22', '2027-03-25'], // Spring 2
              ['2027-04-12', '2027-05-28'], // Summer 1
              ['2027-06-08', '2027-07-21'], // Summer 2
            ];
            const todayStr = new Date().toISOString().split('T')[0];
            const isTermTime = TERM_DATES.some(([s, e]) => todayStr >= s && todayStr <= e);
            const plan = (DAY_PLANS[dayOfWeek] || []).filter(b => !b.termOnly || isTermTime);
            const todayPersonal = [...(datedPersonal.overdue || []), ...(datedPersonal.upcoming || [])].filter(item => item.date === todayStr);
            const personalBlocks = todayPersonal.map(item => ({ time: item.time || '09:00', label: item.title, color: '#9333EA', isPersonal: true, itemId: item.id }));
            const allBlocks = [...plan, ...personalBlocks].sort((a, b) => a.time.localeCompare(b.time));

            return (
              <div className="bg-white border overflow-hidden mb-6" style={{ borderColor: '#E8E0D4' }} data-testid="mini-day-planner">
                {/* Day header */}
                <div className="px-5 py-4" style={{ backgroundColor: '#2C1A0E' }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-medium text-white">
                        {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </p>
                      <p className="text-sm mt-0.5" style={{ color: '#D4AF37' }}>{theme}</p>
                    </div>
                    {weather && !weather.error && (
                      <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        {weather.temperature != null ? `${weather.temperature}°C` : ''}
                      </span>
                    )}
                  </div>
                </div>
                {/* Schedule items — BIG and readable */}
                <div>
                  {allBlocks.map((block, i) => {
                    const bh = parseInt(block.time.split(':')[0]);
                    const bm = parseInt(block.time.split(':')[1] || 0);
                    const nowMins = currentHour * 60 + new Date().getMinutes();
                    const blockMins = bh * 60 + bm;
                    const isPast = blockMins < nowMins - 30;
                    const isCurrent = Math.abs(blockMins - nowMins) <= 30;
                    return (
                      <div key={`blk-${i}`}
                        className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-amber-50/30 transition-colors"
                        style={{ opacity: isPast ? 0.35 : 1, borderBottom: '1px solid #F5F0E8', borderLeft: isCurrent ? '4px solid #D4AF37' : '4px solid transparent' }}
                        onClick={() => block.isPersonal && block.itemId ? openEditModal([...(datedPersonal.overdue||[]),...(datedPersonal.upcoming||[])].find(p=>p.id===block.itemId), 'personal') : null}
                        data-testid={`planner-block-${i}`}
                      >
                        <span className="text-sm font-mono w-14 flex-shrink-0 font-medium" style={{ color: isPast ? '#CBD5E1' : '#8B7355' }}>{block.time}</span>
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: isPast ? '#CBD5E1' : block.color }} />
                        <span className={`text-base ${isPast ? 'line-through' : ''} ${isCurrent ? 'font-semibold' : ''}`} style={{ color: isPast ? '#CBD5E1' : '#2C1A0E' }}>{block.label}</span>
                        {isCurrent && <span className="text-xs px-2 py-1 rounded-full ml-auto font-medium" style={{ backgroundColor: '#D4AF37', color: '#fff' }}>Now</span>}
                      </div>
                    );
                  })}
                  {allBlocks.length === 0 && (
                    <div className="px-5 py-8 text-center text-sm" style={{ color: '#8B7355' }}>No schedule for today</div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ══ TOMORROW + THIS WEEK ══ */}
          <TomorrowGlance />

          {/* End of main content — everything else is in the ☰ menu */}
        </div>
      </div>

      {/* ═══ GLOBAL SEARCH MODAL ═══ */}
      {showSearch && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4" onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults([]); }} data-testid="search-overlay">
          <div className="bg-white w-full max-w-lg rounded-lg shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="search-modal">
            <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid #E8E0D4' }}>
              <Search className="w-4 h-4 flex-shrink-0" style={{ color: '#B8860B' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search tasks, companies, deadlines, events..."
                className="flex-1 text-sm outline-none"
                style={{ color: '#2C1A0E' }}
                autoFocus
                data-testid="search-input"
              />
              {searching && <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" style={{ color: '#B8860B' }} />}
              <button onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults([]); }} className="p-1"><X className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {searchResults.length === 0 && searchQuery.length >= 2 && !searching && (
                <p className="text-xs text-center py-8" style={{ color: '#8B7355' }}>No results for &ldquo;{searchQuery}&rdquo;</p>
              )}
              {searchQuery.length < 2 && !searching && (
                <p className="text-xs text-center py-8" style={{ color: '#8B7355' }}>Type to search across your entire Ops Suite</p>
              )}
              {searchResults.map((r, i) => {
                const Icon = TYPE_ICONS[r.type] || FileText;
                const color = TYPE_COLORS[r.type] || '#8B7355';
                return (
                  <button key={`${r.type}-${r.id}-${i}`} onClick={() => { navigate(r.url); setShowSearch(false); setSearchQuery(''); setSearchResults([]); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-50/40 transition-colors" style={{ borderBottom: '1px solid #F5F1EB' }}
                    data-testid={`search-result-${i}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + '12' }}>
                      <Icon className="w-3.5 h-3.5" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: '#2C1A0E' }}>{r.title}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase" style={{ color }}>{r.type.replace('_', ' ')}</span>
                        {r.subtitle && <span className="text-[10px] truncate" style={{ color: '#8B7355' }}>{r.subtitle}</span>}
                        {r.date && <span className="text-[10px] font-mono flex-shrink-0" style={{ color: '#8B7355' }}>{fmtUK(r.date)}</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: '#D1D5DB' }} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══ EDIT MODAL ═══ */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" onClick={() => setEditModal(null)} data-testid="edit-modal-overlay">
          <div className="bg-white w-full sm:w-[420px] sm:rounded-lg rounded-t-2xl shadow-xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="edit-modal">
            <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid #E8E0D4' }}>
              <h3 className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{editModal.type === 'task' ? 'Edit Task' : 'Edit Item'}</h3>
              <button onClick={() => setEditModal(null)} className="p-1 rounded-full hover:bg-gray-100" data-testid="edit-modal-close"><X className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {/* Title */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>Title</label>
                <input type="text" value={editForm.title || ''} onChange={e => setEditForm({...editForm, title: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="edit-title-input" />
              </div>
              {/* Date + End Date */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>{editModal.type === 'task' ? 'Due Date' : 'Date'}</label>
                  <input type="date" value={editForm[editModal.type === 'task' ? 'due_date' : 'date'] || ''} onChange={e => setEditForm({...editForm, [editModal.type === 'task' ? 'due_date' : 'date']: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="edit-date-input" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>End Date</label>
                  <input type="date" value={editForm.end_date || ''} onChange={e => setEditForm({...editForm, end_date: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="edit-end-date-input" />
                </div>
              </div>
              {/* Start Time + End Time */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>Start Time</label>
                  <input type="time" value={editForm.time || ''} onChange={e => setEditForm({...editForm, time: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="edit-time-input" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>End Time</label>
                  <input type="time" value={editForm.end_time || ''} onChange={e => setEditForm({...editForm, end_time: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="edit-end-time-input" />
                </div>
              </div>
              {/* Status + Priority (tasks only) */}
              <div className={`grid gap-2 ${editModal.type === 'task' ? 'grid-cols-3' : 'grid-cols-1'}`}>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>Status</label>
                  <select value={editForm.status || ''} onChange={e => setEditForm({...editForm, status: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="edit-status-select">
                    {editModal.type === 'task' ? <><option value="open">Open</option><option value="in_progress">In Progress</option><option value="done">Done</option></> : <><option value="active">Active</option><option value="pending">Pending</option><option value="completed">Completed</option></>}
                  </select>
                </div>
                {editModal.type === 'task' && (
                  <>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>Priority</label>
                      <select value={editForm.priority || 'medium'} onChange={e => setEditForm({...editForm, priority: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="edit-priority-select">
                        <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>Category</label>
                      <select value={editForm.category || 'action'} onChange={e => setEditForm({...editForm, category: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="edit-category-select">
                        <option value="action">Action</option><option value="meeting">Meeting</option><option value="event">Event</option><option value="admin">Admin</option><option value="tech">Tech</option><option value="legal">Legal</option><option value="finance">Finance</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              {/* Description / Notes */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider mb-1 font-semibold" style={{ color: '#8B7355' }}>{editModal.type === 'task' ? 'Description' : 'Notes'}</label>
                <textarea value={editForm[editModal.type === 'task' ? 'description' : 'notes'] || ''} onChange={e => setEditForm({...editForm, [editModal.type === 'task' ? 'description' : 'notes']: e.target.value})} className="w-full px-3 py-2 border text-sm rounded-sm resize-none" style={{ borderColor: '#E8E0D4' }} rows={2} data-testid="edit-notes-input" />
              </div>
            </div>
            <div className="flex items-center gap-2 px-5 py-3" style={{ borderTop: '1px solid #E8E0D4' }}>
              <button onClick={handleDeleteItem} className="px-3 py-2 text-xs font-medium rounded-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }} data-testid="edit-delete-btn">Delete</button>
              <div className="flex-1" />
              <button onClick={() => setEditModal(null)} className="px-4 py-2 text-xs font-medium border rounded-sm" style={{ borderColor: '#E8E0D4', color: '#8B7355' }} data-testid="edit-cancel-btn">Cancel</button>
              <button onClick={handleSaveEdit} disabled={saving} className="px-4 py-2 text-xs font-medium rounded-sm" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="edit-save-btn">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-4 z-50 flex flex-col gap-2">
        <label className="w-10 h-10 sm:w-12 sm:h-12 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95 cursor-pointer" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="fab-upload-doc">
          <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
          <input type="file" className="hidden" accept=".pdf,.txt,.csv,.doc,.docx,.png,.jpg,.jpeg,.webp,.heic" onChange={handleDocUpload} />
        </label>
        <button
          onClick={() => setShowQuickAdd(true)}
          className="w-11 h-11 sm:w-14 sm:h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95"
          style={{ backgroundColor: '#B8860B', color: '#FFF' }}
          data-testid="fab-add-task"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>

      {/* Document Parse Review Modal */}
      {showDocParse && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: 'rgba(44,26,14,0.5)' }} onClick={() => { if (!docParsing) { setShowDocParse(false); setParsedItems([]); }}}>
          <div onClick={e => e.stopPropagation()} className="bg-white w-full sm:max-w-lg sm:rounded-lg rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col" data-testid="doc-parse-modal">
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: '#E8E0D4' }}>
              <h3 className="text-sm font-semibold" style={{ color: '#2C1A0E' }}>
                {docParsing ? 'Reading document...' : `${parsedItems.length} items found`}
              </h3>
              {!docParsing && <button onClick={() => { setShowDocParse(false); setParsedItems([]); }}><X className="w-4 h-4" style={{ color: '#8B7355' }} /></button>}
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {docParsing ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#B8860B' }} />
                  <p className="text-xs" style={{ color: '#8B7355' }}>AI is reading your document...</p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {parsedItems.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-sm border" style={{ borderColor: item.selected ? '#D4AF37' : '#E8E0D4', backgroundColor: item.selected ? '#FAF7F2' : '#FFF' }}>
                      <button onClick={() => setParsedItems(prev => prev.map((p, j) => j === i ? { ...p, selected: !p.selected } : p))} className="mt-0.5 flex-shrink-0">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${item.selected ? 'border-amber-600 bg-amber-600' : ''}`} style={{ borderColor: item.selected ? '#B8860B' : '#D1D5DB' }}>
                          {item.selected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium" style={{ color: '#2C1A0E' }}>{item.title}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: item.type === 'task' ? '#D4AF3720' : '#8B735520', color: item.type === 'task' ? '#B8860B' : '#8B7355' }}>{item.type === 'task' ? item.company_name || 'Task' : 'Personal'}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: item.priority === 'high' ? '#DC262620' : item.priority === 'medium' ? '#F59E0B20' : '#10B98120', color: item.priority === 'high' ? '#DC2626' : item.priority === 'medium' ? '#D97706' : '#059669' }}>{item.priority}</span>
                          {item.due_date && <span className="text-[10px]" style={{ color: '#8B7355' }}>{item.due_date}</span>}
                        </div>
                        {item.notes && <p className="text-[10px] mt-1" style={{ color: '#8B7355' }}>{item.notes}</p>}
                      </div>
                    </div>
                  ))}
                  {parsedItems.length === 0 && !docParsing && (
                    <p className="text-xs text-center py-8" style={{ color: '#8B7355' }}>No items extracted — try a clearer photo or different document.</p>
                  )}
                </div>
              )}
            </div>
            {parsedItems.length > 0 && !docParsing && (
              <div className="p-4 border-t flex items-center justify-between" style={{ borderColor: '#E8E0D4' }}>
                <p className="text-[10px]" style={{ color: '#8B7355' }}>{parsedItems.filter(i => i.selected).length} of {parsedItems.length} selected</p>
                <button onClick={handleBulkAdd} disabled={docSaving || !parsedItems.some(i => i.selected)} className="px-6 py-2.5 text-xs uppercase tracking-wider font-medium rounded-sm transition-all disabled:opacity-50" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="bulk-add-submit">
                  {docSaving ? 'Adding...' : `Add ${parsedItems.filter(i => i.selected).length} items`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Add Task Modal */}
      {showQuickAdd && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: 'rgba(44,26,14,0.5)' }} onClick={() => setShowQuickAdd(false)}>
          <form onSubmit={handleQuickAddTask} onClick={e => e.stopPropagation()} className="bg-white w-full sm:max-w-md sm:rounded-lg rounded-t-2xl p-5 shadow-2xl max-h-[85vh] overflow-y-auto" data-testid="quick-add-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold" style={{ color: '#2C1A0E' }}>Quick Add</h3>
              <button type="button" onClick={() => setShowQuickAdd(false)}><X className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            </div>
            {quickParsedItems.length > 0 ? (
              /* AI Parsed Items View */
              <div className="flex flex-col gap-3">
                <p className="text-xs font-medium" style={{ color: '#B8860B' }}>AI extracted {quickParsedItems.length} items:</p>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {quickParsedItems.map((item, i) => (
                    <div key={item.title || `qp-${i}`} className="flex items-start gap-2 p-2 rounded-sm text-xs" style={{ backgroundColor: '#FAF7F2', border: '1px solid #E8E0D4' }}>
                      <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase font-semibold flex-shrink-0" style={{ backgroundColor: item.type === 'personal' ? '#EC489920' : '#D4AF3720', color: item.type === 'personal' ? '#EC4899' : '#B8860B' }}>{item.type}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate" style={{ color: '#2C1A0E' }}>{item.title}</p>
                        <p style={{ color: '#8B7355' }}>{item.company_name || item.category}{item.due_date ? ` · ${item.due_date}` : ''}</p>
                      </div>
                      <button type="button" onClick={() => setQuickParsedItems(prev => prev.filter((_, idx) => idx !== i))} className="flex-shrink-0">
                        <X className="w-3 h-3" style={{ color: '#8B7355' }} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setQuickParsedItems([])} className="flex-1 py-2.5 text-xs uppercase tracking-wider font-medium rounded-sm border" style={{ borderColor: '#E8E0D4', color: '#8B7355' }}>Back</button>
                  <button type="button" onClick={handleBulkAddParsed} disabled={quickBulkSaving}
                    className="flex-1 py-2.5 text-xs uppercase tracking-wider font-medium rounded-sm transition-all disabled:opacity-50"
                    style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="bulk-add-parsed">
                    {quickBulkSaving ? 'Adding...' : `Add All ${quickParsedItems.length} Items`}
                  </button>
                </div>
              </div>
            ) : (
            <div className="flex flex-col gap-3">
              {/* Type toggle: Task / Personal / AI Parse */}
              <div className="flex gap-2">
                {['task', 'personal', 'ai'].map(t => (
                  <button key={t} type="button" onClick={() => { if (t === 'ai') { setAiMode(true); } else { setAiMode(false); setQuickTask(p => ({ ...p, type: t, company_id: '' })); }}}
                    className="flex-1 py-2 text-xs uppercase tracking-wider font-medium rounded-sm border transition-all"
                    style={{
                      borderColor: (t === 'ai' ? aiMode : !aiMode && quickTask.type === t) ? '#B8860B' : '#E8E0D4',
                      backgroundColor: (t === 'ai' ? aiMode : !aiMode && quickTask.type === t) ? '#D4AF3715' : '#FFF',
                      color: (t === 'ai' ? aiMode : !aiMode && quickTask.type === t) ? '#B8860B' : '#8B7355'
                    }}
                    data-testid={`type-${t}`}
                  >{t === 'task' ? 'Company' : t === 'personal' ? 'Personal' : 'AI Parse'}</button>
                ))}
              </div>

              {aiMode ? (
                /* ── AI PARSE MODE ── */
                <div className="flex flex-col gap-3">
                  <div className="p-3 rounded-sm" style={{ backgroundColor: '#FAF7F2', border: '1px solid #D4AF37' }}>
                    <p className="text-xs font-medium mb-1" style={{ color: '#B8860B' }}>Paste any text — email, event details, meeting notes</p>
                    <p className="text-[10px]" style={{ color: '#8B7355' }}>AI will extract tasks, dates, and deadlines automatically</p>
                  </div>
                  <textarea placeholder="Paste text here..." value={quickTextInput}
                    onChange={e => setQuickTextInput(e.target.value)}
                    className="w-full px-3 py-3 text-sm border rounded-sm resize-none"
                    style={{ borderColor: '#D4AF37', color: '#2C1A0E', backgroundColor: '#FFFDF7', minHeight: '120px' }}
                    autoFocus data-testid="quick-add-ai-text" />
                  <button type="button" onClick={handleQuickTextParse} disabled={quickTextParsing || !quickTextInput.trim()}
                    className="w-full py-3 text-xs uppercase tracking-wider font-bold rounded-sm transition-all disabled:opacity-40"
                    style={{ backgroundColor: '#D4AF37', color: '#2C1A0E' }} data-testid="quick-add-ai-parse-btn">
                    {quickTextParsing ? 'AI Extracting...' : 'Extract Tasks with AI'}
                  </button>
                </div>
              ) : (
                /* ── MANUAL ADD MODE ── */
                <>
                  {quickTask.type === 'task' && (
                    <select value={quickTask.company_id} onChange={e => setQuickTask({ ...quickTask, company_id: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border rounded-sm" style={{ borderColor: '#E8E0D4', color: '#2C1A0E' }} required data-testid="quick-add-company">
                      <option value="">Select company...</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  {quickTask.type === 'personal' && (
                    <select value={quickTask.personal_category} onChange={e => setQuickTask({ ...quickTask, personal_category: e.target.value })}
                      className="w-full px-3 py-2.5 text-sm border rounded-sm" style={{ borderColor: '#E8E0D4', color: '#2C1A0E' }} data-testid="quick-add-personal-cat">
                      <option value="admin">Admin</option>
                      <option value="family">Family</option>
                      <option value="kids">Kids</option>
                      <option value="health">Health</option>
                    </select>
                  )}
                  <input type="text" placeholder="What needs doing?" value={quickTask.title}
                    onChange={e => setQuickTask({ ...quickTask, title: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm border rounded-sm" style={{ borderColor: '#E8E0D4', color: '#2C1A0E' }}
                    required autoFocus data-testid="quick-add-title" />
                  <textarea placeholder="Notes (optional)" value={quickTask.description}
                    onChange={e => setQuickTask({ ...quickTask, description: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-sm resize-none" style={{ borderColor: '#E8E0D4', color: '#2C1A0E' }}
                    rows={2} data-testid="quick-add-desc" />
                  <div className="grid grid-cols-4 gap-2">
                    <div><label className="text-[9px] uppercase tracking-wider block mb-1" style={{ color: '#8B7355' }}>Date</label>
                      <input type="date" value={quickTask.due_date} onChange={e => setQuickTask({ ...quickTask, due_date: e.target.value })}
                        className="w-full px-2 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="quick-add-date" /></div>
                    <div><label className="text-[9px] uppercase tracking-wider block mb-1" style={{ color: '#8B7355' }}>Start</label>
                      <input type="time" value={quickTask.time} onChange={e => setQuickTask({ ...quickTask, time: e.target.value })}
                        className="w-full px-2 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="quick-add-time" /></div>
                    <div><label className="text-[9px] uppercase tracking-wider block mb-1" style={{ color: '#8B7355' }}>End</label>
                      <input type="time" value={quickTask.end_time} onChange={e => setQuickTask({ ...quickTask, end_time: e.target.value })}
                        className="w-full px-2 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="quick-add-end-time" /></div>
                    <div><label className="text-[9px] uppercase tracking-wider block mb-1" style={{ color: '#8B7355' }}>End Date</label>
                      <input type="date" value={quickTask.end_date} onChange={e => setQuickTask({ ...quickTask, end_date: e.target.value })}
                        className="w-full px-2 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4' }} data-testid="quick-add-end-date" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <select value={quickTask.priority} onChange={e => setQuickTask({ ...quickTask, priority: e.target.value })}
                      className="px-2 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4', color: '#2C1A0E' }} data-testid="quick-add-priority">
                      <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                    </select>
                    <select value={quickTask.category} onChange={e => setQuickTask({ ...quickTask, category: e.target.value })}
                      className="px-2 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4', color: '#2C1A0E' }} data-testid="quick-add-category">
                      <option value="action">Action</option><option value="meeting">Meeting</option><option value="event">Event</option><option value="admin">Admin</option>
                      <option value="tech">Tech</option><option value="legal">Legal</option><option value="finance">Finance</option>
                    </select>
                    <select value={quickTask.recurring} onChange={e => setQuickTask({ ...quickTask, recurring: e.target.value })}
                      className="px-2 py-2 text-xs border rounded-sm" style={{ borderColor: '#E8E0D4', color: '#2C1A0E' }} data-testid="quick-add-recurring">
                      <option value="">One-off</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <button type="submit" disabled={quickSaving || !quickTask.title || (quickTask.type === 'task' && !quickTask.company_id)}
                    className="w-full py-3 text-xs uppercase tracking-wider font-medium rounded-sm transition-all disabled:opacity-50"
                    style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="quick-add-submit">
                    {quickSaving ? 'Adding...' : quickTask.type === 'personal' ? 'Add Personal Item' : 'Add Task'}
                  </button>
                </>
              )}
            </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default Dashboard;